import React, { useEffect, useState } from 'react';
import { Form, TimePicker } from 'antd';
import EditableCard from 'components/EditableCard';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useShouldUseVerticalLayout } from '../../../../utils/languageUtils';

export interface PickupDeadlineTimeFormData {
  pickupDeadlineTime?: string; // HH:mm格式
  appointmentDeadlineTime?: string; // HH:mm格式 - 预约截止时间
}

interface PickupDeadlineTimeFormProps {
  data: PickupDeadlineTimeFormData;
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: (data: PickupDeadlineTimeFormData) => void;
  onCancel?: () => void;
  title?: string;
  loading?: boolean;
  bordered?: boolean;
}
const PickupDeadlineTimeForm: React.FC<PickupDeadlineTimeFormProps> = ({
  data,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  title,
  loading = false,
  bordered = false,
}) => {
  const { t } = useTranslation();
  const useVerticalLayout = useShouldUseVerticalLayout();
  const [pickupDeadlineTime, setPickupDeadlineTime] = useState<Dayjs | null>(null);
  const [appointmentDeadlineTime, setAppointmentDeadlineTime] = useState<Dayjs | null>(null);

  useEffect(() => {
    // 只在有数据时设置时间，没有数据时保持空值
    if (data.pickupDeadlineTime) {
      setPickupDeadlineTime(dayjs(data.pickupDeadlineTime, 'HH:mm'));
    } else {
      setPickupDeadlineTime(null);
    }

    if (data.appointmentDeadlineTime) {
      setAppointmentDeadlineTime(dayjs(data.appointmentDeadlineTime, 'HH:mm'));
    } else {
      setAppointmentDeadlineTime(null);
    }
  }, [data.pickupDeadlineTime, data.appointmentDeadlineTime]);

  // 监听编辑状态变化，取消编辑时重置为原始数据
  useEffect(() => {
    if (!isEditing) {
      // 重置为原始数据
      if (data.pickupDeadlineTime) {
        setPickupDeadlineTime(dayjs(data.pickupDeadlineTime, 'HH:mm'));
      } else {
        setPickupDeadlineTime(null);
      }

      if (data.appointmentDeadlineTime) {
        setAppointmentDeadlineTime(dayjs(data.appointmentDeadlineTime, 'HH:mm'));
      } else {
        setAppointmentDeadlineTime(null);
      }
    }
  }, [isEditing, data.pickupDeadlineTime, data.appointmentDeadlineTime]);

  const handleSave = () => {
    // 如果用户没有选择时间，保存为undefined
    const pickupTimeValue = pickupDeadlineTime?.format('HH:mm');
    const appointmentTimeValue = appointmentDeadlineTime?.format('HH:mm');

    const formData: PickupDeadlineTimeFormData = {
      pickupDeadlineTime: pickupTimeValue,
      appointmentDeadlineTime: appointmentTimeValue
    };

    onSave?.(formData);
  };

  return (
    <EditableCard
      title={title || t('central.sites.details.pickupDeadlineTime')}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={handleSave}
      onCancel={onCancel}
      loading={loading}
      bordered={bordered}
    >
      {/*
        中文：水平布局（label 与输入同一行）
        外语：垂直布局（label 在上、输入在下），与 Timezone / Geo Coding 一致
      */}
      <Form
        layout={useVerticalLayout ? 'vertical' : 'horizontal'}
        colon={false}
        labelCol={useVerticalLayout ? undefined : { span: 6 }}
        wrapperCol={useVerticalLayout ? undefined : { span: 18 }}
        className="[&_.ant-form-item-control-input]:w-full [&_.ant-form-item-control]:w-full [&_.ant-form-item-control-input-content]:w-full [&_.ant-form-item-label]:text-left [&_.ant-form-item-label>label]:text-left"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-4 [&_.ant-form-item]:mb-0">
          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.appointmentDeadlineTime')}</span>}
          >
          <TimePicker
              value={appointmentDeadlineTime}
              onChange={setAppointmentDeadlineTime}
              format="HH:mm"
              placeholder={t('components.input.selectDate') || ''}
              disabled={!isEditing}
              className="w-full"
              minuteStep={30}
              showNow={false}
          />
          </Form.Item>

          <Form.Item
            label={<span className="font-bold">{t('central.sites.details.packageCutoffTime')}</span>}
            {...(useVerticalLayout ? {} : { labelCol: { span: 5 }, wrapperCol: { span: 20 } })}
          >
          <TimePicker
            value={pickupDeadlineTime}
            onChange={setPickupDeadlineTime}
            format="HH:mm"
              placeholder={t('components.input.selectDate') || ''}
            disabled={!isEditing}
              className="w-full"
            minuteStep={30}
            showNow={false}
          />
          </Form.Item>
        </div>
      </Form>
    </EditableCard>
  );
};

export default PickupDeadlineTimeForm; 